<app-layout-toolbar
  title="Order Detail"
  [crumbs]="(crumbs$ | async) ?? []"
  [backButtonLink]="orderPaths.List">
</app-layout-toolbar>

@let localRecord = localRecord$ | async;

<div class="container p-6">
  @if (order$ | async; as order) {
    @if (localRecord) {
      <div
        class="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 mb-4 text-sm text-default"
        role="status">
        <mat-icon
          svgIcon="mat:cloud_off"
          class="text-amber-600 flex-none"></mat-icon>
        <div>
          <p class="font-semibold m-0">{{ localStatusLabel(localRecord) }}</p>
          <p class="m-0 text-secondary">
            Reference
            <span class="font-mono">{{ localRecord.clientReference }}</span> ·
            recorded {{ localRecord.createdAt | date: "medium" }}
            @if (
              localRecord.provisional.taxEstimated &&
              localRecord.provisional.taxMinor
            ) {
              · tax is an estimate until it syncs
            }
          </p>
        </div>
      </div>
    } @else if (isOffline()) {
      <p class="text-xs text-secondary mb-2 m-0" role="status">
        You're offline: this order is shown as last loaded and can't be changed
        until you reconnect.
      </p>
    }

    <!-- Header card with status + actions -->
    <div class="card p-6 mb-6">
      <div
        class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 class="title m-0">{{ order.orderNumber }}</h2>
          <p class="body-2 text-secondary m-0">
            Placed {{ order.createdAt | date: "medium" }}
          </p>
          <p class="caption text-secondary m-0">
            {{ order.source | removeCharacter: "_" | uppercase }}
            @if (order.fulfillmentMode) {
              · {{ order.fulfillmentMode | removeCharacter: "_" | uppercase }}
            }
          </p>
        </div>
        <app-status-badge
          [text]="statusBadge(order.status).text"
          [color]="statusBadge(order.status).color"></app-status-badge>
      </div>

      <!-- Inline actions -->
      <div
        class="flex flex-wrap gap-2 mt-4 pt-4 border-t border-divider justify-end items-center">
        @if (localRecord) {
          @if (qrPayable(localRecord)) {
            <button
              mat-stroked-button
              color="primary"
              [disabled]="busy"
              (click)="showQr(localRecord)">
              <mat-icon svgIcon="mat:qr_code_2" class="mr-1"></mat-icon>
              Show QR
            </button>
            <button
              mat-button
              [disabled]="busy"
              matTooltip="Copy the payment link to send to the customer"
              (click)="copyQrLink(localRecord)">
              <mat-icon svgIcon="mat:link" class="mr-1"></mat-icon>
              Copy link
            </button>
          }
          @if (canCancelLocal(localRecord)) {
            <button
              mat-button
              class="text-red-500"
              [disabled]="busy"
              (click)="cancelLocal(localRecord)">
              <mat-icon svgIcon="mat:cancel" class="mr-1"></mat-icon>
              Cancel (not yet synced)
            </button>
          } @else {
            <span class="text-sm text-secondary"
              >No actions until this order has synced.</span
            >
          }
        } @else if (isOffline()) {
          <span class="text-sm text-secondary"
            >Actions are available once you're back online.</span
          >
        } @else {
          @if (canInitiatePayment(order)) {
            <button
              mat-flat-button
              color="primary"
              [disabled]="busy"
              (click)="generatePayByLink(order)">
              <mat-icon svgIcon="mat:link" class="mr-1"></mat-icon>
              {{
                canRegenerate(order)
                  ? "Regenerate Pay Link"
                  : "Generate Pay Link"
              }}
            </button>
            @if (false) {
              <button
                mat-stroked-button
                [disabled]="busy"
                (click)="checkout(order)">
                <mat-icon
                  svgIcon="mat:shopping_cart_checkout"
                  class="mr-1"></mat-icon>
                Checkout
              </button>
            }
          }
          @if (order.status === "awaiting_verification") {
            <button
              mat-flat-button
              color="primary"
              [disabled]="busy"
              (click)="verify(order)">
              <mat-icon svgIcon="mat:verified" class="mr-1"></mat-icon>
              Verify Prescription
            </button>
            <button
              mat-button
              class="text-red-500"
              [disabled]="busy"
              (click)="showRejectPrompt()">
              <mat-icon svgIcon="mat:block" class="mr-1"></mat-icon>
              Reject
            </button>
          }
          @if (order.status === "payment_received") {
            <button
              mat-flat-button
              color="primary"
              [disabled]="busy"
              (click)="startPacking(order)">
              <mat-icon svgIcon="mat:inventory_2" class="mr-1"></mat-icon>
              Start Packing
            </button>
          }
          @if (order.status === "packing") {
            <button
              mat-flat-button
              color="primary"
              [disabled]="busy"
              (click)="completePacking(order)">
              <mat-icon svgIcon="mat:check_circle" class="mr-1"></mat-icon>
              Complete Packing
            </button>
          }
          @if (order.status === "packed") {
            <button
              mat-flat-button
              color="primary"
              [disabled]="busy"
              (click)="pickup(order)">
              <mat-icon svgIcon="mat:local_shipping" class="mr-1"></mat-icon>
              Mark Picked Up
            </button>
          }
          @if (order.status === "completed") {
            <a
              mat-flat-button
              color="accent"
              [routerLink]="returnPaths.New"
              [queryParams]="{ orderId: order.id }">
              <mat-icon svgIcon="mat:assignment_return" class="mr-1"></mat-icon>
              Create Return
            </a>
          }
          @if (canCancel(order)) {
            <button
              mat-button
              class="text-red-500"
              [disabled]="busy"
              (click)="showCancelPrompt()">
              <mat-icon svgIcon="mat:cancel" class="mr-1"></mat-icon>
              Cancel Order
            </button>
          }
        }
      </div>

      <!-- Inline cancel form -->
      @if (showCancelForm) {
        <div
          class="border border-divider rounded-lg p-4 mt-4 flex flex-col gap-3"
          [formGroup]="cancelForm">
          <p class="body-2 text-default m-0 font-medium">Cancellation Reason</p>
          <mat-form-field class="w-full">
            <mat-label>Reason</mat-label>
            <textarea
              matInput
              formControlName="reason"
              rows="3"
              placeholder="Enter reason for cancellation"></textarea>
            @if (
              cancelForm.get("reason")?.hasError("required") &&
              cancelForm.get("reason")?.touched
            ) {
              <mat-error>Reason is required</mat-error>
            }
            @if (cancelForm.get("reason")?.hasError("server")) {
              <mat-error>{{
                cancelForm.get("reason")?.getError("server")
              }}</mat-error>
            }
          </mat-form-field>
          <div class="flex gap-2">
            <button
              mat-flat-button
              color="warn"
              (click)="submitCancel(order)"
              class="flex-1">
              Confirm Cancel
            </button>
            <button mat-button (click)="dismissCancel()">Dismiss</button>
          </div>
        </div>
      }

      <!-- Inline reject form -->
      @if (showRejectForm) {
        <div
          class="border border-divider rounded-lg p-4 mt-4 flex flex-col gap-3"
          [formGroup]="rejectForm">
          <p class="body-2 text-default m-0 font-medium">Rejection Reason</p>
          <mat-form-field class="w-full">
            <mat-label>Reason</mat-label>
            <textarea
              matInput
              formControlName="reason"
              rows="3"
              placeholder="Why is this order being rejected?"></textarea>
            @if (
              rejectForm.get("reason")?.hasError("required") &&
              rejectForm.get("reason")?.touched
            ) {
              <mat-error>Reason is required</mat-error>
            }
          </mat-form-field>
          <div class="flex gap-2">
            <button
              mat-flat-button
              color="warn"
              (click)="submitReject(order)"
              class="flex-1">
              Confirm Reject
            </button>
            <button mat-button (click)="dismissReject()">Dismiss</button>
          </div>
        </div>
      }
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <!-- Main column -->
      <div class="md:col-span-2 flex flex-col gap-6">
        <!-- Line items -->
        <div class="card p-6 overflow-x-auto responsive table-wrapper">
          <h3 class="subheading-2 m-0 mb-4">Order Items</h3>
          <table mat-table [dataSource]="order.lines" class="erp-table w-full">
            <ng-container matColumnDef="item">
              <th mat-header-cell *matHeaderCellDef class="text-secondary">
                Item
              </th>
              <td mat-cell *matCellDef="let line">
                {{
                  line.productSnapshotName ??
                    line.variantSnapshotName ??
                    line.variantId
                }}
              </td>
            </ng-container>
            <ng-container matColumnDef="qty">
              <th
                mat-header-cell
                *matHeaderCellDef
                class="text-secondary text-right">
                Qty
              </th>
              <td mat-cell *matCellDef="let line" class="text-right">
                {{ line.quantity }}
              </td>
            </ng-container>
            <ng-container matColumnDef="unitPrice">
              <th
                mat-header-cell
                *matHeaderCellDef
                class="text-secondary text-right">
                Unit Price
              </th>
              <td mat-cell *matCellDef="let line" class="text-right">
                {{ line.unitPriceMinor | amountToMajor | price }}
              </td>
            </ng-container>
            <ng-container matColumnDef="tax">
              <th
                mat-header-cell
                *matHeaderCellDef
                class="text-secondary text-right">
                Tax
              </th>
              <td mat-cell *matCellDef="let line" class="text-right">
                @if (
                  line.lineTaxMinor != null && line.lineTaxMinor != undefined
                ) {
                  {{ line.lineTaxMinor | amountToMajor | price }}
                } @else {
                  —
                }
              </td>
            </ng-container>
            <ng-container matColumnDef="subtotal">
              <th
                mat-header-cell
                *matHeaderCellDef
                class="text-secondary text-right">
                Subtotal
              </th>
              <td
                mat-cell
                *matCellDef="let line"
                class="text-right font-medium">
                {{ line.lineSubtotalMinor | amountToMajor | price }}
              </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="lineColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: lineColumns"></tr>
          </table>

          <!-- Totals -->
          <div
            class="border-t border-divider mt-4 pt-4 flex flex-col items-end gap-1">
            <div class="flex gap-8 body-2 text-secondary">
              <span>Subtotal</span>
              <span>{{ order.subtotalMinor | amountToMajor | price }}</span>
            </div>
            @if (order.lineDiscountTotalMinor || order.cartDiscountMinor) {
              <div class="flex gap-8 body-2 text-secondary">
                <span>Discount</span>
                <span
                  >–{{
                    (order.lineDiscountTotalMinor ?? 0) +
                      (order.cartDiscountMinor ?? 0)
                      | amountToMajor
                      | price
                  }}</span
                >
              </div>
            }
            @if (order.taxTotalMinor) {
              <div class="flex gap-8 body-2 text-secondary">
                <span>VAT</span>
                <span>{{ order.taxTotalMinor | amountToMajor | price }}</span>
              </div>
            }
            <div class="flex gap-8 title">
              <span>Total</span>
              <span>
                @let total = order.totalUserPaymentMinor ?? order.totalMinor;
                {{ total | amountToMajor | price }}</span
              >
            </div>
          </div>
        </div>

        <!-- Clinical Information -->

        <!-- Clinical Information (pharmacy only) -->
        @if (showClinicalInfo(order)) {
          @if (order.clinicalContext; as clinical) {
            <div class="card p-6">
              <h3 class="subheading-2 m-0 mb-1">Clinical Information</h3>
              <p class="caption text-secondary m-0 mb-4">
                Health answers recorded against this order before dispensing.
              </p>

              <div class="flex flex-wrap gap-2 mb-4">
                @for (answer of clinicalAnswers(clinical); track answer.label) {
                  <span
                    class="caption rounded-full px-3 py-1"
                    [class]="
                      answer.value
                        ? 'bg-amber-500/10 text-amber-700'
                        : 'bg-foreground text-secondary border border-divider'
                    ">
                    <mat-icon
                      class="!text-xs !w-3.5 !h-3.5 mr-0.5 align-middle"
                      [svgIcon]="answer.value ? 'mat:check' : 'mat:close'">
                    </mat-icon>
                    {{ answer.label }}
                  </span>
                }
              </div>

              @if (clinical.isOnMedications && clinical.medicationsNote) {
                <div class="rounded-lg border border-divider p-3 mb-3">
                  <p class="text-xs text-secondary font-medium m-0 mb-1">
                    Current medications
                  </p>
                  <p class="body-2 text-default m-0">
                    {{ clinical.medicationsNote }}
                  </p>
                </div>
              }

              <p class="caption text-secondary m-0">
                @if (clinical.recordedBy; as recordedBy) {
                  Recorded by
                  {{ recordedBy.name || recordedBy.email || "the customer" }}
                  ({{
                    recordedBy.userKind === "customer" ? "customer" : "staff"
                  }}) ·
                }
                {{ clinical.updatedAt | date: "medium" }}
              </p>
            </div>
          }
        }

        <!-- Notes -->
        @if (order.notesToCustomer) {
          <div class="card p-6">
            <h3 class="subheading-2 m-0 mb-2">Customer Notes</h3>
            <p class="body-2 text-secondary m-0">{{ order.notesToCustomer }}</p>
          </div>
        }
      </div>

      <!-- Side column -->
      <div class="flex flex-col gap-6">
        <!-- Payment link -->
        @if (paymentLink(order); as link) {
          <div class="card p-6">
            <h3 class="subheading-2 m-0 mb-3">Payment Link</h3>
            @if (order.paymentReference) {
              <p class="caption text-secondary m-0 mb-2">
                Ref: {{ order.paymentReference }}
              </p>
            }
            <div
              class="flex items-center gap-2 rounded-lg border border-divider p-2 mb-3">
              <span class="body-2 text-default truncate flex-auto">{{
                link
              }}</span>
              <button
                mat-icon-button
                matTooltip="Copy link"
                (click)="copyPaymentLink(order)">
                <mat-icon svgIcon="mat:content_copy"></mat-icon>
              </button>
            </div>
            <!-- <a
              mat-stroked-button
              [href]="link"
              target="_blank"
              rel="noopener"
              class="w-full">
              <mat-icon svgIcon="mat:open_in_new" class="mr-1"></mat-icon>
              Open Paystack page
            </a> -->
          </div>
        } @else {
          @if (orderPaymentLink$ | async; as link) {
            @if (
              order.status === "pending_payment" || order.status === "draft"
            ) {
              <div
                class="w-full flex flex-col items-center justify-center gap-2">
                <app-qr-code-card
                  [text]="link"
                  class="w-full max-w-56 aspect-square"></app-qr-code-card>
                <app-url-clipboard [url]="link" />
              </div>
            }
          }
        }

        <!-- Customer info -->
        <div class="card p-6">
          <h3 class="subheading-2 m-0 mb-4">Customer</h3>
          <div class="flex flex-col gap-2">
            <div>
              <p class="caption text-secondary m-0">Name</p>
              <p class="body-2 text-default m-0 font-medium">
                {{ order.checkoutName ?? "—" }}
              </p>
            </div>
            <div>
              <p class="caption text-secondary m-0">Phone</p>
              <p class="body-2 text-default m-0">
                {{ order.checkoutPhone ?? "—" }}
              </p>
            </div>
            @if (order.checkoutEmail) {
              <div>
                <p class="caption text-secondary m-0">Email</p>
                <p class="body-2 text-default m-0">{{ order.checkoutEmail }}</p>
              </div>
            }
          </div>
        </div>

        <!-- Delivery info -->
        <div class="card p-6">
          <h3 class="subheading-2 m-0 mb-4">Fulfillment</h3>
          <div class="flex flex-col gap-2">
            <div>
              <p class="caption text-secondary m-0">Mode</p>
              <p class="body-2 text-default m-0 capitalize">
                {{ order.fulfillmentMode ?? "—" | removeCharacter: "_" }}
              </p>
            </div>
            @if (formatAddress(order); as address) {
              <div>
                <p class="caption text-secondary m-0">Address</p>
                <p class="body-2 text-default m-0">{{ address }}</p>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  } @else {
    @if (loading()) {
      <div class="flex items-center justify-center h-64">
        <app-loading-spinner></app-loading-spinner>
      </div>
    } @else {
      <div class="flex items-center justify-center h-64">
        <p class="body-2 text-secondary">Order not found.</p>
      </div>
    }
  }
</div>
